/**
 * Public short ids — the identifier a record wears in a URL.
 *
 * The alphabet is **Crockford base32**: the digits and the uppercase letters,
 * less `I`, `L`, `O` and `U`. The first three are dropped because they are the
 * pairs people confuse when a URL is read down a phone or copied off a printed
 * luach (`I`/`L` against `1`, `O` against `0`); `U` is dropped so a generated id
 * cannot spell something unfortunate. What survives is unambiguous read aloud,
 * which is the only property that matters for a string an org prints.
 *
 * Ids are RANDOM, never derived from a row's key. Encoding a sequential id
 * (Sqids/Hashids style) makes the value reversible and the set enumerable, and
 * publishes how many records exist.
 */

/** Crockford base32 — 32 symbols, no `I`, `L`, `O` or `U`. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/**
 * Eight symbols ≈ 1.1 × 10¹² values (32⁸). At any org or campaign count this
 * platform will plausibly reach, a collision is rare enough that the unique
 * index plus a regenerate-on-conflict retry never meaningfully fires.
 */
export const SHORT_ID_LENGTH = 8

const DIGITS = '0123456789'

/**
 * Every id carries at least one digit AND at least one letter.
 *
 * This is not cosmetic. The id is the first segment of an org's public URLs
 * (`/{shortId}/zmanim`), so it shares a namespace with the platform's own
 * paths — and a real word never mixes letters with digits. An id that must
 * contain both therefore cannot spell `login`, `preferences`, `about`, or any
 * word we might want to route later.
 *
 * That is a structural guarantee where the alternative is a reserved-word list:
 * a list has to be written before you know which words you will want, has to be
 * kept in step with the router forever, and fails silently and permanently the
 * day someone adds a route without checking it. This cannot fail, and it costs
 * one predicate.
 */
function isWellFormed(id: string): boolean {
  let hasDigit = false
  let hasLetter = false
  for (const ch of id) {
    if (DIGITS.includes(ch)) hasDigit = true
    else hasLetter = true
  }
  return hasDigit && hasLetter
}

/** How many draws before we conclude the generator is broken rather than unlucky. */
const MAX_DRAWS = 100

/**
 * A new short id. Uses rejection sampling over the random bytes rather than
 * `byte % 32`, so every symbol is equally likely — a modulo bias would quietly
 * concentrate ids on the front of the alphabet and cost real keyspace.
 * (32 divides 256 evenly, so nothing is actually rejected here; the guard keeps
 * that true if the alphabet is ever changed.)
 *
 * A draw that is all letters or all digits is discarded and redrawn. All-digits
 * is vanishingly rare; all-letters is about one draw in twenty, so the loop
 * runs about 1.05 times on average. Redrawing rather than patching a digit into
 * a fixed position matters: forcing a symbol would make that position carry
 * less entropy than the others and make ids partly predictable.
 */
export function newShortId(length = SHORT_ID_LENGTH): string {
  const limit = Math.floor(256 / ALPHABET.length) * ALPHABET.length
  for (let draw = 0; draw < MAX_DRAWS; draw++) {
    let out = ''
    while (out.length < length) {
      const bytes = crypto.getRandomValues(new Uint8Array(length))
      for (const b of bytes) {
        if (b >= limit) continue
        out += ALPHABET[b % ALPHABET.length]
        if (out.length === length) break
      }
    }
    if (isWellFormed(out)) return out
  }
  // Unreachable against a working CSPRNG — 100 consecutive rejections has
  // probability ~1e-130. Throwing beats returning a malformed id that would
  // later collide with a route.
  throw new Error('Could not generate a well-formed short id')
}

/**
 * Normalize what someone typed into the canonical form.
 *
 * Uppercases, and applies Crockford's documented input rules — `I` and `L` read
 * as `1`, `O` reads as `0` — so a URL copied by hand still resolves. Hyphens are
 * stripped for the same reason: they are permitted as visual separators.
 * Returns null when anything outside the alphabet survives, so a caller can
 * treat a malformed id as "no such record" without a database round trip.
 *
 * **Text spelled only in letters is a word, never an id**, whatever its letters
 * would read as: the digit rule has to hold of what was TYPED. Corrected first,
 * `settings` reads `SETT1NGS` and passes as an id, which puts a path of ours
 * into an org's address space. Every id we issue carries a digit, so the one
 * thing refused is an id typed with every digit swapped for a look-alike.
 */
export function normalizeShortId(raw: string): string | null {
  if (!/[0-9]/.test(raw)) return null
  const s = raw
    .toUpperCase()
    .replace(/-/g, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0')
  if (s.length !== SHORT_ID_LENGTH) return null
  for (const ch of s) if (!ALPHABET.includes(ch)) return null
  // A string of only letters or only digits cannot be an id we issued, so it is
  // rejected here rather than looked up. This is what lets the router hand any
  // first path segment to this function and treat null as "not an org" — a
  // platform route reaches its own handler without touching the database.
  if (!isWellFormed(s)) return null
  return s
}
